import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { sites } from '@/mocks/sites';
import Breadcrumb from '@/components/base/Breadcrumb';
import SharePanel from '@/pages/detail/components/SharePanel';
import RelatedSites from '@/pages/detail/components/RelatedSites';
import { useFavorites } from '@/hooks/useFavorites';
import { formatDate, getRelatedSites } from '@/utils/siteUtils';

export default function DetailPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const site = sites.find((item) => item.id === siteId);
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  const related = useMemo(() => (site ? getRelatedSites(sites, site, 5) : []), [site]);

  if (!site) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-link-unlink-m text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">未找到该网站</h1>
        <p className="mt-2 text-sm text-foreground-500">该网站可能已下线或链接有误，请返回目录重新查找。</p>
        <Link
          to="/category/all"
          className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          浏览全部网站
        </Link>
      </div>
    );
  }

  const category = categories.find((item) => item.id === site.categoryId);
  const favorited = isFavorite(site.id);

  return (
    <div className="w-full animate-fade-in px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-[1080px]">
        <Breadcrumb
          items={[
            { label: '分类导航', to: '/category/all' },
            { label: category?.name ?? '分类', to: `/category/${site.categoryId}` },
            { label: site.name },
          ]}
        />

        <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="relative h-[150px] bg-secondary-100 md:h-[210px]">
            {site.cover ? (
              <>
                <img
                  src={site.cover}
                  alt={`${site.name} 主图`}
                  title={`${site.name} - 网址大全`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary-500 to-secondary-600">
                <i className="ri-global-line text-5xl text-background-50/70"></i>
              </div>
            )}
          </div>

          <div className="p-5 md:p-7">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100 md:h-20 md:w-20">
                  <img
                    src={site.logo}
                    alt={`${site.name} 网站图标`}
                    title={`${site.name} - 网址大全`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{site.name}</h1>
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700"
                  >
                    <i className="ri-global-line"></i>
                    {site.domain}
                  </a>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                      {category?.name ?? '未分类'}
                    </span>
                    <span className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                      {site.region}
                    </span>
                    <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900">
                      {site.scale}规模
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  访问网站
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <button
                  type="button"
                  onClick={() => toggle(site.id)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? 'border-accent-300 bg-accent-100 text-accent-900'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-700'
                  }`}
                >
                  <i className={favorited ? 'ri-star-fill' : 'ri-star-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-500"></i>
              网站简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">{site.intro}</p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">{site.description}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {site.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  # {tag}
                </Link>
              ))}
            </div>
          </section>

          <aside className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-information-line text-primary-500"></i>
              基本信息
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <dt className="flex-shrink-0 text-foreground-500">网址</dt>
                <dd className="min-w-0 text-right">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-primary-600 hover:text-primary-700"
                  >
                    {site.domain}
                  </a>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">所属分类</dt>
                <dd>
                  <Link to={`/category/${site.categoryId}`} className="text-foreground-800 hover:text-primary-600">
                    {category?.name ?? '未分类'}
                  </Link>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">地区</dt>
                <dd className="text-foreground-800">{site.region}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">收录日期</dt>
                <dd className="text-foreground-800">{formatDate(site.addedAt)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">评分等级</dt>
                <dd className="flex items-center gap-1.5">
                  <span className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`ri-star-fill text-sm ${
                          star <= Math.round(site.rating) ? 'text-accent-500' : 'text-background-300'
                        }`}
                      ></i>
                    ))}
                  </span>
                  <span className="font-medium text-foreground-800">{site.rating.toFixed(1)}</span>
                </dd>
              </div>
            </dl>

            <Link
              to={`/category/${site.categoryId}`}
              className="mt-5 flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回分类页
            </Link>
          </aside>
        </div>

        <RelatedSites list={related} />
      </div>

      <SharePanel open={shareOpen} onClose={() => setShareOpen(false)} siteName={site.name} shareUrl={shareUrl} />
    </div>
  );
}